import { useQuery } from '@apollo/react-hooks'
import { decode } from 'base-64'
import gql from 'graphql-tag'
import { LOCIZE_NAMESPACE } from 'main/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import { Redirect } from 'react-router-dom'

import {
  getLoginUrl,
  getParentJoinUrl,
  getWhitelabelBranding,
} from 'main/components/common/AuthModal/ssoNavigation'
import GenericError from 'main/components/common/GenericError'
import Loader from 'main/components/common/Loader'
import Page from 'main/components/common/Page'
import { useWhitelabel } from 'main/framework'
import { Flags, useFeatureFlag } from 'main/hooks/useFeatureFlag'
import useSSOContext, { INavContext } from 'main/hooks/useSSOContext'
import { logoutPath } from 'main/routes'
import { Col, Grid, Row, Visible } from 'main/ui'
import { UserSignUpSource, UserType } from 'main/util/user'

import { FORM_STATE_KEY } from '@wonderschool/common-base-sso'

import AppFeatures from './components/AppFeatures'
import DownloadApp from './components/DownloadApp'
import Features from './components/Features'
import { SignupForm, SignupFormSSO } from './components/SignupForm'

import { i18nKeyPrefix as PRE } from './'
import { FormSpacer, FormWrapper, Title, TitleWrapper } from './StaffSignUp.styles'

const InviteQuery = gql`
  query LatestInvite($email: String!) {
    latestPendingInvite(email: $email) {
      id
    }
  }
`

function StaffSignUp(props: RouteComponentProps<{ emailCode: string }>) {
  const { emailCode } = props.match.params
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const decodeEmail = () => {
    try {
      const decoded = decode(emailCode)
      return decoded
    } catch (error) {
      return null
    }
  }
  const email = decodeEmail()

  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  const { navContext } = useSSOContext() // this page doesn't set its own nav context but might pass along some
  const whitelabel = useWhitelabel()

  const trackingProps = {
    source: UserSignUpSource.StaffInvitationPage,
  }
  const extra = {
    ...getWhitelabelBranding(whitelabel),
    invitationId: emailCode,
    [FORM_STATE_KEY]: {
      email: email ? email.trim() : '',
    },
  }
  const nextNavContext: INavContext = {
    ...(!!navContext ? navContext : {}),
    route: location.pathname,
  }
  const parentJoinUrl = getParentJoinUrl(trackingProps, nextNavContext, extra)
  const loginUrl = getLoginUrl(trackingProps, nextNavContext, extra, UserType.Parent)
  const logoutUrl = logoutPath()

  function gotoSSOLogin() {
    window.location.assign(loginUrl)
  }

  function gotoSSOParentSignup() {
    window.location.assign(parentJoinUrl)
  }

  function gotoSSOLogout() {
    window.location.assign(logoutUrl)
  }

  function renderForm() {
    if (isSSO) {
      return (
        <SignupFormSSO
          email={email}
          onRequestLogin={gotoSSOLogin}
          onRequestSignup={gotoSSOParentSignup}
          onRequestLogout={gotoSSOLogout}
        />
      )
    } else {
      return <SignupForm email={email} />
    }
  }

  const { data, loading, error } = useQuery(InviteQuery, {
    variables: {
      email,
    },
  })

  if (loading) return <Loader />
  if (error) return <GenericError />

  if (!data || !data.latestPendingInvite) {
    return <Redirect to={'/staff-accept/download-app'} />
  }

  return (
    <Page headerParams={{ onlyShowLogo: true }}>
      <TitleWrapper>
        <Title>{t(`${PRE}-TITLE`)}</Title>
      </TitleWrapper>
      <FormWrapper>
        <Grid>
          <Row>
            <Col lg={6} />
            <Col lg={6}>
              <Visible lg xl>
                <FormSpacer />
              </Visible>
              {renderForm()}
            </Col>
          </Row>
        </Grid>
      </FormWrapper>
      <Features />
      <AppFeatures />
      <DownloadApp />
    </Page>
  )
}

export default StaffSignUp
